/**
 * /admissions/interview/:admissionId — the adaptive N-round Q&A surface.
 *
 * Three modes, picked by the InterviewState the backend returns:
 *
 *   pending_questions.length > 0  → Q&A mode: render the first pending
 *                                   question, record answer + rating
 *   is_round_complete &&
 *      !is_complete                → RoundCompletePanel: teacher picks
 *                                   "Generate next round" (6 more
 *                                   adaptive questions) or "Finalize"
 *                                   (stop here, fire AI scoring)
 *   is_complete                   → FinalizePanel: review per-axis
 *                                   draft scores + notes + decision
 *
 * Survival: every answer is POSTed immediately. A page reload picks up
 * exactly where the interviewer left off. Teacher controls when to
 * stop — no hardcoded round cap. AI scoring is computed by an explicit
 * /prepare-finalize call so it doesn't burn on every poll.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  authHeaders,
  useDeleteInterviewAttachment,
  useFinalizeInterview,
  useGenerateNextInterviewRound,
  useInterviewState,
  usePrepareFinalizeInterview,
  useStudent,
  useSubmitInterviewAnswer,
  useUploadInterviewAttachment,
  type InterviewState,
  type InterviewWrittenTestAttachment,
  type QuickRating,
  type SkillAxis,
} from "../../api/school";

const RATING_TONES: Record<QuickRating, string> = {
  fluent: "bg-emerald-500 hover:bg-emerald-600",
  ok: "bg-sky-500 hover:bg-sky-600",
  struggling: "bg-rose-500 hover:bg-rose-600",
};

const AXES: SkillAxis[] = [
  "listening", "speaking", "reading", "writing", "vocabulary", "grammar",
];

export function InterviewPage() {
  const { t } = useTranslation();
  const { admissionId } = useParams<{ admissionId: string }>();
  const interview = useInterviewState(admissionId);

  if (interview.isLoading)
    return <p className="p-6 text-slate-500 italic">{t("common.loading")}</p>;
  if (interview.error)
    return (
      <div className="m-6 rounded border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {(interview.error as Error).message}
      </div>
    );
  if (!interview.data || !admissionId) return null;

  return (
    <InterviewSurface
      admissionId={admissionId}
      state={interview.data}
    />
  );
}

function InterviewSurface({
  admissionId,
  state,
}: {
  admissionId: string;
  state: InterviewState;
}) {
  const { t } = useTranslation();
  const student = useStudent(state.student_id);

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <header className="mb-6">
        <Link to="/admissions" className="text-sm text-sky-600 hover:underline">
          {t("admissions.interview.backLink")}
        </Link>
        <div className="mt-2 flex items-baseline justify-between gap-4">
          <h1 className="text-2xl font-semibold text-slate-800">
            {student.data?.name_zh ?? t("admissions.title")}{" "}
            {student.data?.name_en && (
              <span className="text-base text-slate-500 ml-1">
                ({student.data.name_en})
              </span>
            )}
          </h1>
          <RoundIndicator state={state} />
        </div>
        <p className="text-xs text-slate-500 mt-1">
          {t("common.grade")} {student.data?.grade ?? "—"} ·{" "}
          {state.is_complete
            ? t("admissions.interview.completed")
            : `${t("admissions.interview.round", { round: state.current_round })} · ${t("admissions.interview.roundRemaining", { count: state.pending_questions.length })}`}
        </p>
      </header>

      {state.is_complete ? (
        <FinalizePanel
          admissionId={admissionId}
          state={state}
          studentId={state.student_id}
        />
      ) : state.is_round_complete ? (
        <RoundCompletePanel admissionId={admissionId} state={state} />
      ) : (
        <QuestionPanel admissionId={admissionId} state={state} />
      )}

      <InterimScores state={state} />

      <WrittenTestPanel admissionId={admissionId} state={state} />
    </div>
  );
}

function WrittenTestPanel({
  admissionId,
  state,
}: {
  admissionId: string;
  state: InterviewState;
}) {
  const { t } = useTranslation();
  const upload = useUploadInterviewAttachment(admissionId);
  const remove = useDeleteInterviewAttachment(admissionId);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const attachments = state.transcript.written_test_attachments ?? [];
  const finalSubmitted = !!state.transcript.final_per_axis_scores;

  function pick() {
    inputRef.current?.click();
  }

  async function onPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      await upload.mutateAsync(f);
    } finally {
      e.target.value = "";
    }
  }

  async function askRemove(idx: number, name: string) {
    if (!window.confirm(t("admissions.interview.writtenTest.confirmRemove", { name }))) return;
    await remove.mutateAsync(idx);
  }

  return (
    <section className="mt-6 rounded-lg border border-slate-200 bg-white p-4">
      <header className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h2 className="text-sm font-semibold text-slate-700">
            {t("admissions.interview.writtenTest.heading")}
          </h2>
          <p className="text-xs text-slate-500 mt-1">
            {t("admissions.interview.writtenTest.hint")}
          </p>
        </div>
        {!finalSubmitted && (
          <button
            type="button"
            onClick={pick}
            disabled={upload.isPending}
            className="px-3 py-1 text-xs font-medium rounded bg-sky-50 text-sky-700 hover:bg-sky-100 disabled:opacity-50 whitespace-nowrap"
          >
            {upload.isPending
              ? t("admissions.interview.writtenTest.uploading")
              : t("admissions.interview.writtenTest.uploadButton")}
          </button>
        )}
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept="image/*,application/pdf,.docx,.doc"
          onChange={onPicked}
        />
      </header>

      {upload.error && (
        <p className="text-xs text-red-600 mb-2">
          {(upload.error as Error).message}
        </p>
      )}
      {remove.error && (
        <p className="text-xs text-red-600 mb-2">
          {(remove.error as Error).message}
        </p>
      )}

      {attachments.length === 0 ? (
        <p className="text-xs text-slate-500 italic">
          {t("admissions.interview.writtenTest.empty")}
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {attachments.map((a: InterviewWrittenTestAttachment, idx: number) => (
            <li key={idx} className="flex items-center gap-3 py-2 text-sm">
              <span className="text-lg">
                {a.mime_type.startsWith("image/") ? "🖼" : "📄"}
              </span>
              <button
                type="button"
                onClick={() =>
                  downloadAttachment(admissionId, idx, a.filename)
                }
                className="flex-1 truncate text-left text-slate-800 hover:text-sky-700"
              >
                {a.filename}
              </button>
              <span className="text-xs text-slate-500 font-mono">
                {Math.round(a.size_bytes / 1024)} KB
              </span>
              {!finalSubmitted && (
                <button
                  type="button"
                  onClick={() => askRemove(idx, a.filename)}
                  disabled={remove.isPending}
                  className="text-xs text-rose-600 hover:underline"
                >
                  {t("admissions.interview.writtenTest.remove")}
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function RoundCompletePanel({
  admissionId,
  state,
}: {
  admissionId: string;
  state: InterviewState;
}) {
  const { t } = useTranslation();
  const nextRound = useGenerateNextInterviewRound(admissionId);
  const prepareFinalize = usePrepareFinalizeInterview(admissionId);
  const busy = nextRound.isPending || prepareFinalize.isPending;
  const err = (nextRound.error || prepareFinalize.error) as Error | undefined;
  return (
    <div className="rounded-lg border border-sky-200 bg-sky-50/60 p-6 mb-4">
      <h2 className="text-lg font-medium text-slate-800 mb-1">
        {t("admissions.interview.roundComplete.title", { round: state.current_round })}
      </h2>
      <p className="text-sm text-slate-600 mb-4">
        {t("admissions.interview.roundComplete.body")}
      </p>
      <div className="flex flex-wrap gap-3 items-center">
        <button
          type="button"
          onClick={() => nextRound.mutate()}
          disabled={busy || !state.can_generate_next_round}
          className="px-4 py-2 rounded bg-white border border-sky-300 text-sky-700 text-sm font-medium hover:bg-sky-100 disabled:opacity-50"
        >
          {nextRound.isPending
            ? t("admissions.interview.roundComplete.generating")
            : t("admissions.interview.roundComplete.generateNext")}
        </button>
        <button
          type="button"
          onClick={() => prepareFinalize.mutate()}
          disabled={busy || !state.can_finalize}
          className="px-4 py-2 rounded bg-sky-500 text-white text-sm font-medium hover:bg-sky-600 disabled:opacity-50"
        >
          {prepareFinalize.isPending
            ? t("admissions.interview.roundComplete.preparingFinalize")
            : t("admissions.interview.roundComplete.finalize")}
        </button>
        {err && (
          <span className="text-xs text-red-600">{err.message}</span>
        )}
      </div>
    </div>
  );
}

function RoundIndicator({ state }: { state: InterviewState }) {
  const { t } = useTranslation();
  const rounds = state.transcript.rounds ?? [];
  let answered = 0;
  for (const r of rounds) {
    for (const q of r.questions) {
      if (q.interviewer_quick_rating !== null) answered++;
    }
  }
  return (
    <div className="text-xs text-slate-500">
      <span
        dangerouslySetInnerHTML={{
          __html: t("admissions.interview.progress", {
            answered: `<span class="font-mono text-slate-800">${answered}</span>`,
            rounds: `<span class="font-mono text-slate-800">${rounds.length}</span>`,
          }),
        }}
      />
    </div>
  );
}

// ── Question card (one axis at a time) ─────────────────────────────

function QuestionPanel({
  admissionId,
  state,
}: {
  admissionId: string;
  state: InterviewState;
}) {
  const { t } = useTranslation();
  const submit = useSubmitInterviewAnswer(admissionId);
  const current = state.pending_questions[0];
  const [answer, setAnswer] = useState("");

  // Reset textarea when the active question changes.
  useEffect(() => {
    setAnswer("");
  }, [current?.axis]);

  if (!current) {
    // Server is between rounds — refetch will arrive momentarily.
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500 italic">
        {t("admissions.interview.generatingNextRound")}
      </div>
    );
  }

  async function send(rating: QuickRating) {
    if (!current) return;
    await submit.mutateAsync({
      round: state.current_round,
      axis: current.axis,
      student_answer: answer.trim(),
      interviewer_quick_rating: rating,
    });
    setAnswer("");
  }

  // Round-2 questions carry an adaptive difficulty hint. Surface it as
  // a small chip so the interviewer knows AI escalated/relaxed this axis.
  const difficulty = state.transcript.rounds
    ?.find((r) => r.round === state.current_round)
    ?.questions.find((q) => q.axis === current.axis)?.difficulty;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-xs font-medium uppercase tracking-wide text-sky-600">
          {t(`common.skills.${current.axis}`)}
        </span>
        {difficulty && state.current_round >= 2 && (
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded ${
              difficulty === "harder"
                ? "bg-amber-100 text-amber-700"
                : difficulty === "easier"
                  ? "bg-sky-100 text-sky-700"
                  : "bg-slate-100 text-slate-600"
            }`}
          >
            {t(`admissions.interview.difficulty.${difficulty}`)}
          </span>
        )}
      </div>
      <p className="text-base text-slate-800 leading-relaxed mb-4">
        {current.question}
      </p>

      <textarea
        value={answer}
        onChange={(e) => setAnswer(e.target.value)}
        placeholder={t("admissions.interview.answerPlaceholder")}
        rows={3}
        className="w-full px-3 py-2 text-sm rounded border border-slate-200 mb-3"
      />

      <div className="flex items-center justify-between gap-3 mt-2">
        <p className="text-xs text-slate-500">
          {t("admissions.interview.ratingPrompt")}
        </p>
        <div className="flex gap-2">
          {(Object.keys(RATING_TONES) as QuickRating[]).map((r) => (
            <button
              key={r}
              type="button"
              disabled={submit.isPending}
              onClick={() => send(r)}
              className={`px-3 py-1.5 text-xs text-white rounded ${RATING_TONES[r]} disabled:opacity-50`}
            >
              {t(`admissions.interview.ratings.${r}`)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Interim per-axis scores ────────────────────────────────────────

function InterimScores({ state }: { state: InterviewState }) {
  const { t } = useTranslation();
  return (
    <section className="rounded-lg border border-slate-200 bg-slate-50/60 p-4">
      <h2 className="text-xs font-medium uppercase tracking-wide text-slate-500 mb-2">
        {t("admissions.interview.interimScores")}
      </h2>
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
        {AXES.map((axis) => (
          <div key={axis} className="rounded bg-white border border-slate-200 px-2 py-1.5">
            <div className="text-[10px] text-slate-500">{t(`common.skills.${axis}`)}</div>
            <div className="font-mono text-sm text-slate-800">
              {state.interim_per_axis_scores[axis] ?? "—"}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── Finalize panel ─────────────────────────────────────────────────

function FinalizePanel({
  admissionId,
  state,
}: {
  admissionId: string;
  state: InterviewState;
  studentId: string;
}) {
  const { t } = useTranslation();
  const finalize = useFinalizeInterview(admissionId);

  // Seed editable scores from the interim aggregate so the interviewer
  // is reviewing AI's draft rather than typing from scratch.
  const initialScores = useMemo(() => {
    const out = {} as Record<SkillAxis, number>;
    for (const axis of AXES) {
      out[axis] = state.interim_per_axis_scores[axis] ?? 60;
    }
    return out;
  }, [state.interim_per_axis_scores]);

  // ai_scoring is the structured-output blob the scorer agent persists
  // into the transcript when is_complete first becomes true.
  const aiScoring = (state.transcript as Record<string, unknown>).ai_scoring as
    | { overall_score?: number; ai_recommendation?: "accept" | "reject" | "borderline"; summary?: string }
    | undefined;

  const [scores, setScores] = useState<Record<SkillAxis, number>>(initialScores);
  const [notes, setNotes] = useState(aiScoring?.summary ?? "");

  const overall = useMemo(() => {
    const vals = Object.values(scores);
    return Math.round(vals.reduce((a, b) => a + b, 0) / vals.length);
  }, [scores]);

  // Prefer AI's recommendation if it ran (it weights productive axes +
  // applies the floor-on-receptive-skills rule). Fall back to the simple
  // overall-bucket if AI didn't fire.
  const aiRecommendation: "accept" | "reject" | "borderline" =
    aiScoring?.ai_recommendation ??
    (overall >= 70 ? "accept" : overall >= 50 ? "borderline" : "reject");

  async function doFinalize() {
    await finalize.mutateAsync({
      per_axis_scores: scores,
      overall_score: overall,
      interviewer_notes: notes.trim() || null,
      ai_recommendation: aiRecommendation,
    });
    // Hand off to the pool view — the candidate now sits in the
    // 已面试 · 待决策 sub-section of 招生候选, where the principal /
    // decision-maker actions 录取 or 婉拒. Keeping interviewer and
    // decision-maker as two separate moments mirrors the real Bosong
    // workflow, where the teacher who ran the interview isn't always
    // the same person who signs off on admissions.
    window.location.href = "/admissions";
  }

  const ready = !!state.transcript.final_per_axis_scores;

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6 mb-4">
      <h2 className="text-lg font-medium text-slate-800 mb-1">{t("admissions.interview.finalize.title")}</h2>
      <p className="text-xs text-slate-500 mb-4">
        {t("admissions.interview.finalize.description")}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
        {AXES.map((axis) => (
          <label key={axis} className="block text-sm">
            <span className="block text-xs text-slate-600 mb-1">
              {t(`common.skills.${axis}`)}
            </span>
            <input
              type="number"
              min={0}
              max={100}
              value={scores[axis]}
              onChange={(e) =>
                setScores({ ...scores, [axis]: Number(e.target.value) || 0 })
              }
              disabled={ready}
              className="w-full px-2 py-1.5 rounded border border-slate-200 font-mono"
            />
          </label>
        ))}
        <div className="rounded bg-sky-50 border border-sky-200 px-3 py-2">
          <div className="text-xs text-sky-700">{t("admissions.interview.finalize.overallAuto")}</div>
          <div className="font-mono text-2xl text-sky-800">{overall}</div>
        </div>
      </div>

      <label className="block text-sm mb-4">
        <span className="block text-xs text-slate-600 mb-1">{t("admissions.interview.finalize.notesLabel")}</span>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          disabled={ready}
          placeholder={t("admissions.interview.finalize.notesPlaceholder")}
          className="w-full px-3 py-2 text-sm rounded border border-slate-200"
        />
      </label>

      <div className="flex items-center gap-3 text-xs text-slate-600 mb-4">
        <span>{t("admissions.interview.finalize.aiSuggestionLabel")}</span>
        <span
          className={`px-2 py-0.5 rounded font-medium ${
            aiRecommendation === "accept"
              ? "bg-emerald-100 text-emerald-700"
              : aiRecommendation === "reject"
                ? "bg-rose-100 text-rose-700"
                : "bg-amber-100 text-amber-700"
          }`}
        >
          {aiRecommendation === "accept"
            ? t("admissions.interview.finalize.aiRecAccept")
            : aiRecommendation === "reject"
              ? t("admissions.interview.finalize.aiRecReject")
              : t("admissions.interview.finalize.aiRecBorderline")}
        </span>
      </div>

      {!ready ? (
        <button
          type="button"
          onClick={doFinalize}
          disabled={finalize.isPending}
          className="px-4 py-2 text-sm font-medium rounded bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50"
        >
          {finalize.isPending
            ? t("admissions.interview.finalize.submitting")
            : t("admissions.interview.finalize.submit")}
        </button>
      ) : (
        <div className="border-t border-slate-200 pt-4 mt-4 text-sm text-slate-600">
          {t("admissions.interview.finalize.submittedNote")}
        </div>
      )}
    </div>
  );
}

/** Bearer-authed download for a written-test attachment. The
 *  school-backend endpoint streams the bytes; we wrap them in a Blob
 *  and trigger a same-tab download with the original filename. Plain
 *  <a href> would 401 since the link click flow doesn't carry headers. */
async function downloadAttachment(
  admissionId: string,
  index: number,
  filename: string,
): Promise<void> {
  const url = `/api/school/admissions/interview/${admissionId}/attachments/${index}`;
  const resp = await fetch(url, { headers: authHeaders() });
  if (!resp.ok) {
    window.alert(`Download failed: HTTP ${resp.status}`);
    return;
  }
  const blob = await resp.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = objectUrl;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
}
